import React, { useState, useEffect } from 'react';
import { Plus, Type, Code, Image, Trash2, MoveUp, MoveDown, Upload } from 'lucide-react';
import { highlightSQL } from '../utils/sqlHighlight';

const TextEditor = ({ content, onChange }) => {
  const [text, setText] = useState(content || '');
  const [showToolbar, setShowToolbar] = useState(false);

  const updateText = (newText) => {
    setText(newText);
    onChange(newText);
  };

  const insertFormat = (format) => {
    const textarea = document.querySelector('.text-block:focus') || document.querySelector('.text-block');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = text.substring(start, end);
      let newText;
      let newCursorPos = end;

      switch (format) {
        case 'bold':
          if (selectedText) {
            newText = text.substring(0, start) + `**${selectedText}**` + text.substring(end);
            newCursorPos = end + 4;
          } else {
            newText = text.substring(0, start) + '****' + text.substring(end);
            newCursorPos = start + 2;
          }
          break;
        case 'italic':
          if (selectedText) {
            newText = text.substring(0, start) + `*${selectedText}*` + text.substring(end);
            newCursorPos = end + 2;
          } else {
            newText = text.substring(0, start) + '**' + text.substring(end);
            newCursorPos = start + 1;
          }
          break;
        case 'bullet':
          const lines = text.split('\n');
          const lineIndex = text.substring(0, start).split('\n').length - 1;
          if (!lines[lineIndex].startsWith('• ')) {
            lines[lineIndex] = '• ' + lines[lineIndex];
          }
          newText = lines.join('\n');
          break;
        case 'number':
          const numLines = text.split('\n');
          const numLineIndex = text.substring(0, start).split('\n').length - 1;
          if (!numLines[numLineIndex].match(/^\d+\. /)) {
            numLines[numLineIndex] = '1. ' + numLines[numLineIndex];
          }
          newText = numLines.join('\n');
          break;
        default:
          return;
      }
      
      updateText(newText);
      
      // Restore cursor position
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  };

  return (
    <div className="text-editor">
      <div className="text-toolbar">
        <button type="button" onClick={() => insertFormat('bold')} className="toolbar-btn" title="Bold">
          <strong>B</strong>
        </button>
        <button type="button" onClick={() => insertFormat('italic')} className="toolbar-btn" title="Italic">
          <em>I</em>
        </button>
        <button type="button" onClick={() => insertFormat('bullet')} className="toolbar-btn" title="Bullet List">
          •
        </button>
        <button type="button" onClick={() => insertFormat('number')} className="toolbar-btn" title="Numbered List">
          1.
        </button>
      </div>
      <textarea
        value={text}
        onChange={(e) => updateText(e.target.value)}
        placeholder="Enter your text content...\n\nFormatting tips:\n**bold text**\n*italic text*\n• bullet points\n1. numbered lists"
        className="text-block"
        rows="6"
        onFocus={() => setShowToolbar(true)}
        ref={(el) => { if (el) el.textareaRef = el; }}
      />
    </div>
  );
};

const TableEditor = ({ content, onChange }) => {
  const [tableData, setTableData] = useState(() => {
    return content || {
      headers: ['Column 1', 'Column 2'],
      rows: [['Data 1', 'Data 2']]
    };
  });

  const updateTable = (newData) => {
    setTableData(newData);
    onChange(newData);
  };

  const addColumn = () => {
    const newData = {
      headers: [...tableData.headers, `Column ${tableData.headers.length + 1}`],
      rows: tableData.rows.map(row => [...row, ''])
    };
    updateTable(newData);
  };

  const addRow = () => {
    const newData = {
      ...tableData,
      rows: [...tableData.rows, new Array(tableData.headers.length).fill('')]
    };
    updateTable(newData);
  };

  const updateHeader = (index, value) => {
    const newHeaders = [...tableData.headers];
    newHeaders[index] = value;
    updateTable({ ...tableData, headers: newHeaders });
  };

  const updateCell = (rowIndex, colIndex, value) => {
    const newRows = [...tableData.rows];
    newRows[rowIndex][colIndex] = value;
    updateTable({ ...tableData, rows: newRows });
  };

  return (
    <div className="table-editor">
      <div className="table-controls">
        <button type="button" onClick={addColumn} className="btn-sm btn-secondary">
          Add Column
        </button>
        <button type="button" onClick={addRow} className="btn-sm btn-secondary">
          Add Row
        </button>
      </div>
      <div className="table-container">
        <table className="editor-table">
          <thead>
            <tr>
              {tableData.headers.map((header, index) => (
                <th key={index}>
                  <input
                    type="text"
                    value={header}
                    onChange={(e) => updateHeader(index, e.target.value)}
                    className="table-header-input"
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, colIndex) => (
                  <td key={colIndex}>
                    <input
                      type="text"
                      value={cell}
                      onChange={(e) => updateCell(rowIndex, colIndex, e.target.value)}
                      className="table-cell-input"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const RichEditor = ({ content, onChange }) => {
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [blocks, setBlocks] = useState(() => {
    if (Array.isArray(content) && content.length > 0) {
      return content;
    }
    if (typeof content === 'string' && content) {
      return content.split('\n\n').map((text, index) => ({
        id: Date.now() + index,
        type: 'text',
        content: text
      }));
    }
    return [{ id: Date.now(), type: 'text', content: '' }];
  });

  // Update blocks when content prop changes
  useEffect(() => {
    if (Array.isArray(content) && content.length > 0) {
      setBlocks(content);
    } else if (typeof content === 'string' && content) {
      setBlocks(content.split('\n\n').map((text, index) => ({
        id: Date.now() + index,
        type: 'text',
        content: text
      })));
    }
  }, [content]);

  const updateBlocks = (newBlocks) => {
    setBlocks(newBlocks);
    onChange(newBlocks);
  };

  const addBlock = (type, index) => {
    const newBlock = {
      id: Date.now(),
      type,
      content: type === 'image' 
        ? { src: '', alt: '', caption: '' }
        : type === 'table'
        ? { headers: ['Column 1', 'Column 2'], rows: [['Data 1', 'Data 2']] }
        : type === 'heading'
        ? { content: '', level: 'h2' }
        : ''
    };
    const newBlocks = [...blocks];
    newBlocks.splice(index + 1, 0, newBlock);
    updateBlocks(newBlocks);
  };

  const updateBlock = (id, content) => {
    const newBlocks = blocks.map(block =>
      block.id === id ? { ...block, content } : block
    );
    updateBlocks(newBlocks);
  };

  const deleteBlock = (id) => {
    if (blocks.length > 1) {
      updateBlocks(blocks.filter(block => block.id !== id));
    }
  };

  const moveBlock = (id, direction) => {
    const index = blocks.findIndex(block => block.id === id);
    if ((direction === 'up' && index > 0) || (direction === 'down' && index < blocks.length - 1)) {
      const newBlocks = [...blocks];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      [newBlocks[index], newBlocks[targetIndex]] = [newBlocks[targetIndex], newBlocks[index]];
      updateBlocks(newBlocks);
    }
  };

  const handleImageUpload = (blockId, file) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const block = blocks.find(b => b.id === blockId);
        updateBlock(blockId, {
          ...block.content,
          src: e.target.result,
          alt: file.name
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const parseMarkdown = (markdown) => {
    const lines = markdown.split('\n');
    const blocks = [];
    let currentBlock = null;
    let inCodeBlock = false;
    let codeLanguage = '';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Code blocks
      if (line.startsWith('```')) {
        if (inCodeBlock) {
          if (currentBlock) {
            blocks.push(currentBlock);
            currentBlock = null;
          }
          inCodeBlock = false;
        } else {
          inCodeBlock = true;
          codeLanguage = line.substring(3).trim();
          currentBlock = {
            id: Date.now() + i,
            type: codeLanguage === 'sql' ? 'sql' : 'text',
            content: ''
          };
        }
        continue;
      }
      
      if (inCodeBlock) {
        currentBlock.content += (currentBlock.content ? '\n' : '') + line;
        continue;
      }
      
      // Headings
      if (line.startsWith('#')) {
        const level = line.match(/^#+/)[0].length;
        blocks.push({
          id: Date.now() + i,
          type: 'heading',
          content: { content: line.substring(level).trim(), level: `h${Math.min(level, 4)}` }
        });
        continue;
      }
      
      // Empty lines
      if (!line.trim()) {
        if (currentBlock && currentBlock.content) {
          blocks.push(currentBlock);
          currentBlock = null;
        }
        continue;
      }
      
      // Regular text
      if (!currentBlock) {
        currentBlock = {
          id: Date.now() + i,
          type: 'text',
          content: ''
        };
      }
      
      currentBlock.content += (currentBlock.content ? '\n' : '') + line;
    }
    
    if (currentBlock) {
      blocks.push(currentBlock);
    }
    
    return blocks.length > 0 ? blocks : [{ id: Date.now(), type: 'text', content: '' }];
  };

  const handleFileUpload = (file) => {
    if (file.name.endsWith('.md')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const markdown = e.target.result;
        const parsedBlocks = parseMarkdown(markdown);
        updateBlocks(parsedBlocks);
      };
      reader.readAsText(file);
    } else {
      alert('Currently only .md (Markdown) files are supported. Please convert your document to Markdown format.');
    }
  };

  return (
    <div className="rich-editor">
      <div className="editor-header">
        <div className="upload-section">
          <input
            type="file"
            accept=".md,.txt"
            onChange={(e) => handleFileUpload(e.target.files[0])}
            className="file-input"
            id="document-upload"
            style={{ display: 'none' }}
          />
          <label htmlFor="document-upload" className="btn btn-secondary">
            <Upload size={16} />
            Upload Markdown File
          </label>
          <button 
            type="button" 
            onClick={() => setShowGuidelines(!showGuidelines)} 
            className="btn btn-info"
          >
            📋 Format Guidelines
          </button>
        </div>
        
        {showGuidelines && (
          <div className="guidelines-panel">
            <h4>Document Format Guidelines</h4>
            
            <div className="format-section">
              <h5>📄 Markdown (.md) Files - Recommended</h5>
              <div className="format-rules">
                <strong>Headings:</strong>
                <code># Main Heading</code><br/>
                <code>## Sub Heading</code><br/>
                <code>### Sub Sub Heading</code>
                
                <strong>Text Formatting:</strong>
                <code>**bold text**</code><br/>
                <code>*italic text*</code><br/>
                <code>- bullet points</code><br/>
                <code>1. numbered lists</code>
                
                <strong>Code Blocks:</strong>
                <code>```sql</code><br/>
                <code>SELECT * FROM users;</code><br/>
                <code>```</code>
                
                <strong>Tables:</strong>
                <code>| Header 1 | Header 2 |</code><br/>
                <code>|----------|----------|</code><br/>
                <code>| Data 1   | Data 2   |</code>
              </div>
            </div>
            
            <div className="format-section">
              <h5>📝 Word (.docx) Files - Future Support</h5>
              <div className="format-rules">
                <strong>Use Built-in Styles:</strong><br/>
                • Heading 1, Heading 2, Heading 3 styles<br/>
                • Bold/Italic formatting<br/>
                • Bullet and numbered lists<br/>
                • Insert tables using Word's table feature<br/>
                • Embed images directly in document
                
                <div className="note">Note: .docx support coming soon. Use .md files for now.</div>
              </div>
            </div>
            
            <div className="format-section">
              <h5>✅ Best Practices</h5>
              <div className="format-rules">
                • Use clear, descriptive headings<br/>
                • Separate code blocks with proper language tags<br/>
                • Keep paragraphs concise and readable<br/>
                • Use bullet points for lists<br/>
                • Add alt text for images<br/>
                • Test SQL scripts before including them
              </div>
            </div>
          </div>
        )}
      </div>
      {blocks.map((block, index) => (
        <div key={block.id} className="editor-block">
          <div className="block-controls">
            <div className="block-actions">
              <button
                type="button"
                onClick={() => moveBlock(block.id, 'up')}
                disabled={index === 0}
                className="btn-icon"
              >
                <MoveUp size={14} />
              </button>
              <button
                type="button"
                onClick={() => moveBlock(block.id, 'down')}
                disabled={index === blocks.length - 1}
                className="btn-icon"
              >
                <MoveDown size={14} />
              </button>
              <button
                type="button"
                onClick={() => deleteBlock(block.id)}
                disabled={blocks.length === 1}
                className="btn-icon btn-danger"
              >
                <Trash2 size={14} />
              </button>
            </div>
            
            <div className="add-block-menu">
              <button
                type="button"
                onClick={() => addBlock('text', index)}
                className="btn-icon"
                title="Add Text"
              >
                <Type size={14} />
              </button>
              <button
                type="button"
                onClick={() => addBlock('heading', index)}
                className="btn-icon"
                title="Add Heading"
              >
                <Type size={14} style={{ fontWeight: 'bold' }} />
              </button>
              <button
                type="button"
                onClick={() => addBlock('sql', index)}
                className="btn-icon"
                title="Add SQL Script"
              >
                <Code size={14} />
              </button>
              <button
                type="button"
                onClick={() => addBlock('image', index)}
                className="btn-icon"
                title="Add Image"
              >
                <Image size={14} />
              </button>
              <button
                type="button"
                onClick={() => addBlock('table', index)}
                className="btn-icon"
                title="Add Table"
              >
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3h18v18H3z"/>
                  <path d="M21 9H3"/>
                  <path d="M21 15H3"/>
                  <path d="M12 3v18"/>
                </svg>
              </button>
            </div>
          </div>

          <div className="block-content">
            {block.type === 'heading' && (
              <div className="heading-block">
                <select
                  value={block.level || 'h2'}
                  onChange={(e) => updateBlock(block.id, { content: block.content, level: e.target.value })}
                  className="heading-level"
                >
                  <option value="h1">H1</option>
                  <option value="h2">H2</option>
                  <option value="h3">H3</option>
                  <option value="h4">H4</option>
                </select>
                <input
                  type="text"
                  value={block.content || ''}
                  onChange={(e) => updateBlock(block.id, { content: e.target.value, level: block.level || 'h2' })}
                  placeholder="Enter heading text..."
                  className="heading-input"
                />
              </div>
            )}

            {block.type === 'text' && (
              <TextEditor
                content={block.content}
                onChange={(content) => updateBlock(block.id, content)}
              />
            )}

            {block.type === 'sql' && (
              <div className="sql-block">
                <div className="sql-header">
                  <span className="sql-label">SQL Script</span>
                </div>
                <textarea
                  value={block.content}
                  onChange={(e) => updateBlock(block.id, e.target.value)}
                  placeholder="Enter your SQL script here..."
                  className="sql-textarea"
                  rows="10"
                />
                {block.content && (
                  <div className="sql-preview">
                    <div className="sql-preview-header">Preview:</div>
                    <pre
                      className="sql-highlighted"
                      dangerouslySetInnerHTML={{
                        __html: highlightSQL(block.content)
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {block.type === 'image' && (
              <div className="image-block">
                <div className="image-upload">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(block.id, e.target.files[0])}
                    className="image-input"
                    id={`image-${block.id}`}
                  />
                  <label htmlFor={`image-${block.id}`} className="image-upload-label">
                    <Image size={24} />
                    Choose Image
                  </label>
                </div>
                
                {block.content?.src && (
                  <div className="image-preview">
                    <img src={block.content.src} alt={block.content.alt} className="preview-image" />
                    <input
                      type="text"
                      value={block.content.alt || ''}
                      onChange={(e) => updateBlock(block.id, { ...block.content, alt: e.target.value })}
                      placeholder="Alt text (for accessibility)"
                      className="image-alt"
                    />
                    <input
                      type="text"
                      value={block.content.caption || ''}
                      onChange={(e) => updateBlock(block.id, { ...block.content, caption: e.target.value })}
                      placeholder="Image caption (optional)"
                      className="image-caption"
                    />
                  </div>
                )}
              </div>
            )}

            {block.type === 'table' && (
              <TableEditor
                content={block.content}
                onChange={(content) => updateBlock(block.id, content)}
              />
            )}
          </div>
        </div>
      ))}
      
      <div className="add-first-block">
        <button
          type="button"
          onClick={() => addBlock('text', blocks.length - 1)}
          className="btn btn-secondary"
        >
          <Plus size={16} />
          Add Content Block
        </button>
      </div>
    </div>
  );
};

export default RichEditor;